import React, { useEffect, useMemo, useState } from 'react';
import './App.css';

import memberstackDOM from '@memberstack/dom';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import AppShell from './components/Layout/AppShell';

import CommandCenter from './components/Dashboard/CommandCenter';
import ARGOSPage from './components/StrategyPages/ARGOSPage';
import GEMICPage from './components/StrategyPages/GEMICPage';
import ACCEPage from './components/StrategyPages/ACCEPage';
import MEICPage from './components/StrategyPages/MEICPage';
import MarketIntelligencePage from './components/StrategyPages/MarketIntelligencePage';

import MarketIntelligenceWebsocketClient from './components/Clients/MarketIntelligenceWebsocketClient';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'memberstack';

const allowedPlanIds = [
  // Premium Sellers / VIP / free trial plans that should access this dashboard

  'pln_free-trial-k2e50rfn',
  'pln_legacy-membership-337d0n3j',
  'pln_spx-gamma-7-day-free-trial-ny3f01hl',
  'pln_vip-membership-c0ea0lhm',
  'pln_premium-seller-membership-q2en0as4',
  'pln_7-day-membership-zd1d088k',
];

const REDIRECTS = {
  login: 'https://www.spxgamma.com/log-in',
  join: 'https://www.spxgamma.com/pricing',
  denied: 'https://www.spxgamma.com/membership/denied',
};

function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase();
}

function isValidStatus(status) {
  const normalized = normalizeStatus(status);
  return normalized === 'active' || normalized === 'trialing';
}

function getValidPlan(member) {
  if (!member?.planConnections || member.planConnections.length === 0) {
    return null;
  }

  return member.planConnections.find((connection) => {
    const priceId = connection.payment?.priceId;
    const planId = connection.planId;

    const isAllowed =
      allowedPlanIds.includes(priceId) || allowedPlanIds.includes(planId);

    return isAllowed && isValidStatus(connection.status);
  });
}

function buildManualCredentials() {
  const memberId = process.env.REACT_APP_LOCAL_MEMBER_ID;
  const planId = process.env.REACT_APP_LOCAL_PLAN_ID;
  const status = normalizeStatus(
    process.env.REACT_APP_LOCAL_PLAN_STATUS || 'active'
  );

  if (!memberId || !planId || !status) {
    throw new Error(
      'Manual auth mode requires REACT_APP_LOCAL_MEMBER_ID, REACT_APP_LOCAL_PLAN_ID, and REACT_APP_LOCAL_PLAN_STATUS.'
    );
  }

  return {
    type: 'AUTH',
    memberId,
    planId,
    status,
  };
}

function App() {
  const [loading, setLoading] = useState(true);
  const [memberCredentials, setMemberCredentials] = useState(null);
  const [authError, setAuthError] = useState(null);

  const memberstack = useMemo(() => {
    if (AUTH_MODE === 'manual') {
      return null;
    }

    return memberstackDOM.init({
      publicKey: process.env.REACT_APP_MEMBERSTACK_PUBLIC_KEY,
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function authenticateMember() {
      try {
        if (AUTH_MODE === 'manual') {
          const credentials = buildManualCredentials();

          if (!isMounted) return;

          setMemberCredentials(credentials);
          setLoading(false);
          return;
        }

        const { data: member } = await memberstack.getCurrentMember();

        if (!member) {
          window.location.href = REDIRECTS.login;
          return;
        }

        const validPlan = getValidPlan(member);

        if (!validPlan) {
          const hasPlans =
            member.planConnections && member.planConnections.length > 0;

          window.location.href = hasPlans ? REDIRECTS.denied : REDIRECTS.join;
          return;
        }

        const validId = allowedPlanIds.includes(validPlan.payment?.priceId)
          ? validPlan.payment.priceId
          : validPlan.planId;

        const credentials = {
          type: 'AUTH',
          memberId: member.id,
          planId: validId,
          status: normalizeStatus(validPlan.status),
        };

        if (!isMounted) return;

        setMemberCredentials(credentials);
        setLoading(false);
      } catch (error) {
        console.error('[AUTH] Authentication error:', error);

        if (AUTH_MODE === 'manual') {
          if (!isMounted) return;

          setAuthError(error.message);
          setLoading(false);
          return;
        }

        window.location.href = REDIRECTS.login;
      }
    }

    authenticateMember();

    return () => {
      isMounted = false;
    };
  }, [memberstack]);

  if (loading) {
    return <div className="appLoading">Loading...</div>;
  }

  if (authError) {
    return <div className="appLoading">Auth error: {authError}</div>;
  }

  return (
    <BrowserRouter>
      <AppShell>
        {/* Global MIS stream client.
            Keep mounted above routes so it stays connected across the dashboard. */}
        {memberCredentials && (
          <MarketIntelligenceWebsocketClient
            memberCredentials={memberCredentials}
          />
        )}

        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<CommandCenter />} />
          <Route path="/argos" element={<ARGOSPage />} />
          <Route path="/gemic" element={<GEMICPage />} />
          <Route path="/acce" element={<ACCEPage />} />
          <Route path="/meic" element={<MEICPage />} />
          <Route
            path="/market-intelligence"
            element={<MarketIntelligencePage />}
          />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

export default App;






// Local development code.
// import './App.css';
// import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
//
// import AppShell from './components/Layout/AppShell';
//
// import CommandCenter from './components/Dashboard/CommandCenter';
// import ARGOSPage from './components/StrategyPages/ARGOSPage';
// import GEMICPage from './components/StrategyPages/GEMICPage';
// import ACCEPage from './components/StrategyPages/ACCEPage';
// import MEICPage from './components/StrategyPages/MEICPage';
// import MarketIntelligencePage from './components/StrategyPages/MarketIntelligencePage';
//
// import MarketIntelligenceWebsocketClient from './components/Clients/MarketIntelligenceWebsocketClient';
//
// function App() {
//   return (
//     <BrowserRouter>
//       <AppShell>
//         {/* Global MIS stream client.
//             Keep mounted above routes so it stays connected across the dashboard. */}
//         <MarketIntelligenceWebsocketClient />
//
//         <Routes>
//           <Route path="/" element={<Navigate to="/dashboard" replace />} />
//           <Route path="/dashboard" element={<CommandCenter />} />
//           <Route path="/argos" element={<ARGOSPage />} />
//           <Route path="/gemic" element={<GEMICPage />} />
//           <Route path="/acce" element={<ACCEPage />} />
//           <Route path="/meic" element={<MEICPage />} />
//           <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
//         </Routes>
//       </AppShell>
//     </BrowserRouter>
//   );
// }
//
// export default App;





